/**
 * Add Ethereum network to MetaMask wallet
 * @param {Object} network - Network object with chainId, rpcUrl, name, nativeCurrency, blockExplorerUrl
 * @returns {Promise<boolean>} - True if successful
 */
export const addNetworkToMetaMask = async (network) => {
  if (!window.ethereum) {
    throw new Error('MetaMask not detected');
  }

  if (!network?.chainId || !network?.rpcUrl) {
    throw new Error('Network requires chainId and rpcUrl');
  }

  try {
    const chainIdHex = `0x${parseInt(network.chainId).toString(16)}`;

    const params = {
      chainId: chainIdHex,
      chainName: network.name || `Chain ${network.chainId}`,
      rpcUrls: [network.rpcUrl],
      nativeCurrency: {
        name: network.nativeCurrency || 'ETH',
        symbol: network.nativeCurrency || 'ETH',
        decimals: network.nativeCurrencyDecimals || 18,
      },
    };

    // Add block explorer URL if available
    if (network.blockExplorerUrl) {
      params.blockExplorerUrls = [network.blockExplorerUrl];
    }

    console.log('[web3Utils] Adding network to MetaMask:', {
      chainId: network.chainId,
      name: network.name,
      rpcUrl: network.rpcUrl,
    });

    const result = await window.ethereum.request({
      method: 'wallet_addEthereumChain',
      params: [params],
    });

    console.log('[web3Utils] Network added successfully');
    return true;
  } catch (error) {
    // Error code 4001 = user rejected
    // Error code -32603 = internal error (usually means chain already exists)
    if (error.code === 4001) {
      throw new Error('User rejected adding network');
    }

    // If chain already exists, this is not a fatal error
    if (error.message?.includes('chainId') || error.code === -32603) {
      console.warn('[web3Utils] Chain may already exist in wallet');
      return true;
    }

    console.error('[web3Utils] Failed to add network:', error);
    throw error;
  }
};

/**
 * Switch to a specific Ethereum network in MetaMask
 * @param {Object} network - Network object with chainId
 * @returns {Promise<boolean>} - True if successful
 */
export const switchToNetwork = async (network) => {
  if (!window.ethereum) {
    throw new Error('MetaMask not detected');
  }

  if (!network?.chainId) {
    throw new Error('Network requires chainId');
  }

  try {
    const chainIdHex = `0x${parseInt(network.chainId).toString(16)}`;

    console.log('[web3Utils] Switching to network:', {
      chainId: network.chainId,
      chainIdHex,
    });

    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: chainIdHex }],
    });

    console.log('[web3Utils] Network switched successfully');
    return true;
  } catch (error) {
    // Error code 4902 = chain not added to wallet
    if (error.code === 4902) {
      console.warn('[web3Utils] Chain not found in MetaMask, will need to add it first');
      throw new Error('CHAIN_NOT_FOUND');
    }

    // Error code 4001 = user rejected
    if (error.code === 4001) {
      throw new Error('User rejected network switch');
    }

    console.error('[web3Utils] Failed to switch network:', error);
    throw error;
  }
};

/**
 * Add network to MetaMask and switch to it
 * @param {Object} network - Network object
 * @returns {Promise<boolean>} - True if successful
 */
export const addAndSwitchNetwork = async (network) => {
  try {
    // Try to switch first
    try {
      await switchToNetwork(network);
      return true;
    } catch (switchError) {
      // If chain not found, add it first
      if (switchError.message === 'CHAIN_NOT_FOUND') {
        console.log('[web3Utils] Chain not found, adding it first...');
        await addNetworkToMetaMask(network);

        // Now try switching again
        await switchToNetwork(network);
        return true;
      }

      // If user rejected or other error, throw
      throw switchError;
    }
  } catch (error) {
    console.error('[web3Utils] Add and switch network failed:', error);
    throw error;
  }
};
